import { useSite } from '../lib/SiteContentContext';
import { Award, FlaskConical } from 'lucide-react';
import { imageAttrs, resolveImageUrl } from '../lib/imageUrl';

// Tutors are shown in a two-column grid at md+.
const TUTOR_SIZES = '(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw';

/**
 * Deliberate branded panel used when a tutor doesn't want a photograph.
 * Reads as a designed profile card, not an unfinished placeholder.
 */
function BrandedPanel({ subject, boards }) {
  return (
    <div className="relative w-full h-full bg-gradient-to-br from-brand-ink to-brand-ink/90 overflow-hidden">
      {/* Subtle chemistry line-art background */}
      <svg
        aria-hidden="true"
        className="absolute inset-0 w-full h-full opacity-[0.14]"
        viewBox="0 0 400 500"
        fill="none"
        preserveAspectRatio="xMidYMid slice"
      >
        <defs>
          <pattern id="hex-grid" x="0" y="0" width="80" height="70" patternUnits="userSpaceOnUse">
            <path
              d="M20 5 L60 5 L80 35 L60 65 L20 65 L0 35 Z"
              stroke="#FFC200"
              strokeWidth="1"
              fill="none"
            />
          </pattern>
        </defs>
        <rect width="400" height="500" fill="url(#hex-grid)" />
        {/* Sample molecule diagram */}
        <g stroke="#FFC200" strokeWidth="2" fill="none">
          <circle cx="200" cy="220" r="14" />
          <circle cx="140" cy="260" r="10" />
          <circle cx="260" cy="260" r="10" />
          <circle cx="200" cy="300" r="10" />
          <circle cx="200" cy="160" r="10" />
          <line x1="200" y1="234" x2="200" y2="290" />
          <line x1="188" y1="212" x2="150" y2="252" />
          <line x1="212" y1="212" x2="250" y2="252" />
          <line x1="200" y1="206" x2="200" y2="170" />
        </g>
      </svg>

      {/* Content */}
      <div className="relative z-10 h-full w-full flex flex-col items-center justify-center px-6 text-center">
        <div className="w-14 h-14 rounded-2xl bg-brand-sun grid place-items-center shadow-lg">
          <FlaskConical className="w-7 h-7 text-brand-ink" aria-hidden="true" />
        </div>
        <div className="mt-5 text-brand-sun font-display text-2xl md:text-3xl tracking-wide leading-tight">
          {subject}
        </div>
        <div className="mt-2 text-brand-cream/85 text-xs md:text-sm font-semibold uppercase tracking-[0.25em]">
          {boards}
        </div>
      </div>
    </div>
  );
}

export default function TutorsSection() {
  const { tutors } = useSite();
  return (
    <section className="py-14 md:py-20 bg-white" aria-labelledby="tutors-heading">
      <div className="container">
        <div className="max-w-none">
          <div className="text-brand-coral text-sm font-semibold uppercase tracking-widest">
            Meet the team
          </div>
          <h2
            id="tutors-heading"
            className="mt-2 font-display text-3xl sm:text-4xl md:text-5xl text-brand-ink md:whitespace-nowrap"
          >
            Qualified teachers.{' '}
            <span className="italic text-brand-teal">Real classroom experience.</span>
          </h2>
          <p className="mt-3 text-brand-ink/70 text-base md:text-lg">
            Meet the qualified UK teachers behind Science Sorted&rsquo;s GCSE and A Level science programmes.
          </p>
        </div>

        <div className="mt-8 md:mt-10 grid sm:grid-cols-2 gap-6 md:gap-8 max-w-4xl">
          {tutors.map((t) => (
            <article
              key={t.id}
              className="group relative rounded-3xl overflow-hidden bg-brand-cream border border-brand-ink/10 hover:shadow-xl transition-all"
              data-testid={`tutor-card-${t.id}`}
            >
              <div className="aspect-[4/5] overflow-hidden bg-brand-butter/50 grid place-items-center">
                {t.image ? (
                  t.photoStyle ? (
                    <div
                      role="img"
                      aria-label={`Portrait of ${t.name}`}
                      className="w-full h-full bg-no-repeat transition-transform duration-500 group-hover:scale-[1.03]"
                      style={{
                        backgroundImage: `url(${resolveImageUrl(t.image, 800)})`,
                        ...t.photoStyle,
                      }}
                    />
                  ) : (
                    <img
                      {...imageAttrs(t.image, { sizes: TUTOR_SIZES, fallbackWidth: 800 })}
                      alt={`Portrait of ${t.name}`}
                      className="w-full h-full transition-transform duration-500 group-hover:scale-[1.03]"
                      loading="lazy"
                      style={{
                        objectFit: 'cover',
                        objectPosition: t.photoPosition || '50% 15%',
                        transform: t.photoScale ? `scale(${t.photoScale})` : 'none',
                        transformOrigin: t.photoOrigin || '50% 20%',
                      }}
                    />
                  )
                ) : t.brandedPanel ? (
                  <BrandedPanel
                    subject={t.brandedPanel.subject}
                    boards={t.brandedPanel.boards}
                  />
                ) : null}
              </div>
              <div className="p-5 md:p-6">
                <h3 className="font-display text-xl md:text-2xl text-brand-ink">{t.name}</h3>
                <p className="text-sm text-brand-teal font-semibold mt-1">{t.role}</p>
                <p className="mt-3 text-brand-ink/75 text-sm leading-relaxed">{t.bio}</p>
                {t.bioExtra && (
                  <p className="mt-2 text-brand-ink/75 text-sm leading-relaxed">{t.bioExtra}</p>
                )}
                {t.credentials && t.credentials.length > 0 && (
                  <ul className="mt-4 flex flex-wrap gap-1.5">
                    {t.credentials.map((c) => (
                      <li
                        key={c}
                        className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-widest bg-brand-sun/90 text-brand-ink px-2 py-1 rounded-full"
                      >
                        <Award className="w-3 h-3" aria-hidden="true" /> {c}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
